package fr.manyverse.app.presentation.components

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.RowScope
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.selection.toggleable
import androidx.compose.material3.Checkbox
import androidx.compose.material3.LocalContentColor
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Switch
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.semantics.Role
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.unit.dp

/**
 * Ligne « libellé + interrupteur » entièrement cliquable (motif Material 3 des listes de réglages).
 *
 * Toute la ligne porte le `toggleable` : le libellé devient une zone tactile, et TalkBack/VoiceOver
 * annoncent le libellé avec l'état de l'interrupteur en un seul élément. Le [Switch] interne reçoit
 * donc `onCheckedChange = null` pour ne pas créer une seconde cible concurrente.
 */
@Composable
fun SwitchRow(
    label: String,
    checked: Boolean,
    onCheckedChange: (Boolean) -> Unit,
    modifier: Modifier = Modifier,
    enabled: Boolean = true,
    textStyle: TextStyle = MaterialTheme.typography.bodyMedium
) {
    ToggleRow(checked, onCheckedChange, enabled, Role.Switch, modifier) {
        ToggleLabel(label, enabled, textStyle, Modifier.weight(1f))
        Switch(checked = checked, onCheckedChange = null, enabled = enabled)
    }
}

/**
 * Ligne « case à cocher + libellé » entièrement cliquable — voir [SwitchRow].
 */
@Composable
fun CheckboxRow(
    label: String,
    checked: Boolean,
    onCheckedChange: (Boolean) -> Unit,
    modifier: Modifier = Modifier,
    enabled: Boolean = true,
    textStyle: TextStyle = MaterialTheme.typography.bodyMedium
) {
    ToggleRow(checked, onCheckedChange, enabled, Role.Checkbox, modifier) {
        Checkbox(checked = checked, onCheckedChange = null, enabled = enabled)
        ToggleLabel(label, enabled, textStyle, Modifier.weight(1f))
    }
}

/**
 * Modificateur à appliquer à une ligne personnalisée contenant une [Checkbox] (passée avec
 * `onCheckedChange = null`), quand [CheckboxRow] ne suffit pas (ex. ligne avec image).
 */
fun Modifier.checkboxToggleable(
    checked: Boolean,
    onCheckedChange: (Boolean) -> Unit,
    enabled: Boolean = true
): Modifier = toggleable(value = checked, enabled = enabled, role = Role.Checkbox, onValueChange = onCheckedChange)

@Composable
private fun ToggleRow(
    checked: Boolean,
    onCheckedChange: (Boolean) -> Unit,
    enabled: Boolean,
    role: Role,
    modifier: Modifier,
    content: @Composable RowScope.() -> Unit
) {
    Row(
        modifier = modifier
            .fillMaxWidth()
            .clip(MaterialTheme.shapes.small)
            .toggleable(value = checked, enabled = enabled, role = role, onValueChange = onCheckedChange)
            // Le Switch/Checkbox sans onCheckedChange perd sa taille interactive minimale : la ligne la garantit
            .heightIn(min = 48.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(8.dp),
        content = content
    )
}

@Composable
private fun ToggleLabel(label: String, enabled: Boolean, style: TextStyle, modifier: Modifier) {
    Text(
        text = label,
        style = style,
        // Opacité « désactivé » de Material 3 sur la couleur de contenu héritée (dialogue, surface…)
        color = if (enabled) LocalContentColor.current else LocalContentColor.current.copy(alpha = 0.38f),
        modifier = modifier
    )
}
